/**
 * Tab manager component
 * Handles tab switching and content rendering
 */

import type { TabId, TabConfig } from '../../types';

type TabChangeCallback = (tabId: TabId, container: HTMLElement) => void;

export class TabManager {
  private tabsContainer: HTMLElement;
  private contentContainer: HTMLElement;
  private currentTab: TabId | null = null;
  private onTabChange: TabChangeCallback;

  constructor(
    tabsContainer: HTMLElement,
    contentContainer: HTMLElement,
    onTabChange: TabChangeCallback
  ) {
    this.tabsContainer = tabsContainer;
    this.contentContainer = contentContainer;
    this.onTabChange = onTabChange;
  }

  render(): void {
    const tabs = this.getTabConfig();

    this.tabsContainer.innerHTML = tabs
      .map((tab) => {
        const classes = [
          'tab',
          tab.requiresTurnstile ? 'requires-turnstile' : '',
          tab.requiresAuth ? 'requires-auth' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return `<button class="${classes}" data-tab="${tab.id}">${tab.label}</button>`;
      })
      .join('');

    // Setup click listeners
    this.tabsContainer.querySelectorAll('.tab').forEach((button) => {
      button.addEventListener('click', (e) => {
        const tabId = (e.target as HTMLElement).dataset.tab as TabId;
        if (tabId) {
          this.showTab(tabId);
        }
      });
    });
  }

  showTab(tabId: TabId): void {
    // Update active tab visual state
    this.tabsContainer.querySelectorAll('.tab').forEach((tab) => {
      tab.classList.toggle('active', tab.getAttribute('data-tab') === tabId);
    });

    // Clear content container
    this.contentContainer.innerHTML = '';

    // Notify callback to render new content
    this.currentTab = tabId;
    this.onTabChange(tabId, this.contentContainer);
  }

  getCurrentTab(): TabId | null {
    return this.currentTab;
  }

  private getTabConfig(): TabConfig[] {
    return [
      { id: 'signup', label: 'Signup', requiresTurnstile: true },
      { id: 'login', label: 'Login' },
      { id: 'verify-email', label: 'Verify Email' },
      { id: 'forgot-password', label: 'Forgot Password', requiresTurnstile: true },
      { id: 'reset-password', label: 'Reset Password', requiresTurnstile: true },
      { id: 'resend-code', label: 'Resend Code' },
      { id: 'refresh', label: 'Refresh Token' },
      { id: 'validate', label: 'Validate Token', requiresAuth: true },
      { id: 'signout', label: 'Sign Out', requiresAuth: true },
      { id: 'invalidate-sessions', label: 'Invalidate All', requiresAuth: true },
      { id: 'upgrade', label: 'Upgrade Payment', requiresAuth: true },
      { id: 'cancel-subscription', label: 'Cancel Subscription', requiresAuth: true },
      { id: 'model-config', label: '🤖 Model Config', requiresAuth: true },
      { id: 'task-routing', label: '📋 Task Routing', requiresAuth: true },
      { id: 'ai-routing', label: '🌍 AI Routing', requiresAuth: true },
      { id: 'tier-enforcement', label: '🎯 Tier Enforcement', requiresAuth: true },
      { id: 'service-clients', label: '🛡️ Service Clients', requiresAuth: true },
      { id: 'routing-visualizer', label: '🔍 Chain Visualizer', requiresAuth: true },
      { id: 'streaming-chat', label: 'Streaming Chat', requiresAuth: true },
      { id: 'single-prompt', label: 'Single Prompt', requiresAuth: true },
      { id: 'tts', label: 'TTS Test', requiresAuth: true },
      { id: 'tts-streaming', label: '🔊 TTS Stream Debug', requiresAuth: true },
      { id: 'livekit-test', label: '📡 LiveKit Test', requiresAuth: true },
      { id: 'game-generation', label: '🎮 Game Generation', requiresAuth: false },
    ];
  }
}
